import { useSiteSetupActions } from "@inrange/building-manager-api-client";
import {
  Site,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { useState } from "react";
import { Container } from "react-bootstrap";
import CollapsibleCard from "./CollapsibleCard";
import DemoOperationalActualsSection from "./siteSections/DemoOperationalActualsSection";
import HealthMonitoringConfigModal from "./siteSections/HealthMonitoringConfigModal";
import DataIngestionConfigModal from "./siteSections/DataIngestionConfigModal";
import MonitoringConfigSection from "./siteSections/MonitoringConfigSection";
import MonitoringPortalsCard from "./siteSections/MonitoringPortalsCard";
import SoracomSimSection from "./siteSections/SoracomSimSection";
import TablesStatusSection from "./siteSections/TablesStatusSection";

const SiteHealthSetup = ({
  site,
  isDemo,
  esysEntries,
  ingestionByEsys,
  simStatusByEsys,
  registerSoracomSim,
  importSoracomSim,
  fetchReadingsTables,
  createReadingsTables,
  populateDemoActuals,
  discoverReduxiDevices,
  updateIngestion,
  deleteIngestion,
}: {
  site: Site;
  isDemo: boolean;
  esysEntries: [string, SiteElectricalSystem][];
  ingestionByEsys: ReturnType<typeof useSiteSetupActions>["ingestionByEsys"];
  simStatusByEsys: ReturnType<typeof useSiteSetupActions>["simStatusByEsys"];
  registerSoracomSim: ReturnType<typeof useSiteSetupActions>["registerSoracomSim"];
  importSoracomSim: ReturnType<typeof useSiteSetupActions>["importSoracomSim"];
  fetchReadingsTables: ReturnType<typeof useSiteSetupActions>["fetchReadingsTables"];
  createReadingsTables: ReturnType<typeof useSiteSetupActions>["createReadingsTables"];
  populateDemoActuals: ReturnType<typeof useSiteSetupActions>["populateDemoActuals"];
  discoverReduxiDevices: ReturnType<typeof useSiteSetupActions>["discoverReduxiDevices"];
  updateIngestion: ReturnType<typeof useSiteSetupActions>["updateIngestion"];
  deleteIngestion: ReturnType<typeof useSiteSetupActions>["deleteIngestion"];
}) => {
  const [showIngestionModalForEsys, setShowIngestionModalForEsys] = useState<
    string | null
  >(null);
  const [showHealthConfigModal, setShowHealthConfigModal] = useState(false);

  return (
    <Container fluid style={{ padding: "12px 12px 2rem 12px" }} className="d-flex flex-column gap-3">
      <CollapsibleCard
        title="Site monitoring setup"
        initialOpen={true}
        className="mt-3"
        isBulkEdit={true}
      >
        <div className="d-flex flex-column gap-3">
          {!isDemo && (
            <>
              <SoracomSimSection
                site={site}
                esysEntries={esysEntries}
                ingestionByEsys={ingestionByEsys}
                simStatusByEsys={simStatusByEsys}
                registerSoracomSim={registerSoracomSim}
                importSoracomSim={importSoracomSim}
              />
              <hr className="my-0" />
            </>
          )}
          <TablesStatusSection
            fetchReadingsTables={fetchReadingsTables}
            createReadingsTables={createReadingsTables}
          />
          {isDemo && (
            <>
              <hr className="my-0" />
              <DemoOperationalActualsSection
                populateDemoActuals={populateDemoActuals}
              />
            </>
          )}
          {!isDemo && (
            <>
              <hr className="my-0" />
              <MonitoringConfigSection
                site={site}
                esysEntries={esysEntries}
                ingestionByEsys={ingestionByEsys}
                onConfigureEsys={setShowIngestionModalForEsys}
                onConfigureHealth={() => setShowHealthConfigModal(true)}
              />
            </>
          )}
        </div>
      </CollapsibleCard>

      {!isDemo && (
        <MonitoringPortalsCard
          esysEntries={esysEntries}
          ingestionByEsys={ingestionByEsys}
        />
      )}

      {!isDemo && showIngestionModalForEsys && (
        <DataIngestionConfigModal
          site={site}
          selectedEsysUuid={showIngestionModalForEsys}
          existingConfig={
            ingestionByEsys[showIngestionModalForEsys]?.data?.site &&
            !!ingestionByEsys[showIngestionModalForEsys]!.data!.site
              .has_monitoring_config
              ? ingestionByEsys[showIngestionModalForEsys]!.data!.site
              : null
          }
          onSave={async (config) => {
            await updateIngestion.mutateAsync(config);
            setShowIngestionModalForEsys(null);
          }}
          onDelete={
            ingestionByEsys[showIngestionModalForEsys]?.data?.site &&
            !!ingestionByEsys[showIngestionModalForEsys]!.data!.site
              .has_monitoring_config
              ? async () => {
                  await deleteIngestion.mutateAsync(
                    showIngestionModalForEsys
                  );
                  setShowIngestionModalForEsys(null);
                }
              : undefined
          }
          isSaving={updateIngestion.isLoading}
          isDeleting={deleteIngestion.isLoading}
          onClose={() => setShowIngestionModalForEsys(null)}
          discoverReduxiDevices={discoverReduxiDevices}
        />
      )}
      {!isDemo && showHealthConfigModal && (
        <HealthMonitoringConfigModal
          site={site}
          onClose={() => setShowHealthConfigModal(false)}
        />
      )}
    </Container>
  );
};

export default SiteHealthSetup;
